import LimitSourceBadge from './LimitSourceBadge';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getBackendURL } from '../../utils/backendURL';

export default function TeamCapacityPanel({ user, onChanged }) {
  const [state, setState] = useState(null);
  const [active, setActive] = useState([]);
  const [preferred, setPreferred] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const apply = data => {
    setState(data); setActive(data.active_user_ids);
    setPreferred(data.preferred_user_ids.length ? data.preferred_user_ids : data.active_user_ids);
  };
  useEffect(() => {
    let mounted = true;
    const load = () => axios.get(`${getBackendURL()}/api/team/capacity`)
      .then(({ data }) => { if (mounted) apply(data); })
      .catch(e => { if (mounted && e.response?.status !== 404) setError('Could not load team capacity.'); });
    load();
    return () => { mounted = false; };
  }, [user.id]);
  const toggle = (ids, id) => ids.includes(id) ? ids.filter(value => value !== id) : [...ids, id];
  const save = async mode => {
    setSaving(true); setError('');
    try {
      const body = mode === 'active' ? { active_user_ids: active } : { preferred_user_ids: preferred };
      const { data } = await axios.put(`${getBackendURL()}/api/team/capacity`, body);
      apply(data); onChanged?.();
    } catch (e) { setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not save member selection.'); }
    finally { setSaving(false); }
  };
  if (!state) return error ? <p role="alert">{error}</p> : null;
  const owner = user.id === state.owner_id;
  const exceeds = state.limit !== -1 && active.length > state.limit;
  const postTrialExcess = state.trial_ends_at && state.limit_after_trial !== -1 && state.members.length > state.limit_after_trial;
  return <section className="bg-white border rounded-lg p-5 space-y-3" aria-label="Team capacity">
    <h3 className="text-lg font-semibold">Organization access</h3>
    <p>{state.active_user_ids.length} active / {state.limit === -1 ? 'Unlimited' : state.limit} seats · <LimitSourceBadge source={state.limit_source} /></p>
    {state.trial_ends_at && <p className="text-amber-800">Trial ends {new Date(state.trial_ends_at + (/Z$/.test(state.trial_ends_at) ? '' : 'Z')).toLocaleDateString()}.
      {' '}Without a paid plan, the next member limit is {state.limit_after_trial === -1 ? 'Unlimited' : state.limit_after_trial}.</p>}
    {postTrialExcess && <p className="text-amber-800">Some members will lose access after the trial. Choose who has priority below, or upgrade the plan.</p>}
    <p className="text-sm text-gray-600">The owner always keeps a seat. Suspended members keep their data. Only the owner can change this selection.</p>
    <div className="overflow-x-auto"><table className="w-full text-sm text-left">
      <thead><tr><th>Member</th><th>Access</th><th>Active now</th><th>Keep first on reduction</th></tr></thead>
      <tbody>{state.members.map(member => <tr key={member.id} className="border-t">
        <td className="py-3">{member.email}{member.is_owner ? ' (owner)' : ''}</td>
        <td>{member.access_status === 'active' ? 'Active' : 'Suspended by member limit'}</td>
        <td><input type="checkbox" aria-label={`Active ${member.email}`} checked={active.includes(member.id)}
          disabled={!owner || member.is_owner || saving} onChange={() => setActive(toggle(active, member.id))} /></td>
        <td><input type="checkbox" aria-label={`Keep ${member.email}`} checked={preferred.includes(member.id)}
          disabled={!owner || member.is_owner || saving} onChange={() => setPreferred(toggle(preferred, member.id))} /></td>
      </tr>)}</tbody></table></div>
    <p className="text-xs text-gray-600">Preferred members are kept first, in the order selected; remaining seats use signup order.</p>
    {exceeds && <p role="alert" className="text-red-600">Select at most {state.limit} active members.</p>}
    {error && <p role="alert" className="text-red-600">{error}</p>}
    {owner && <div className="flex gap-3">
      <button disabled={saving || exceeds} onClick={() => save('active')} className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50">Save active members</button>
      <button disabled={saving} onClick={() => save('preferred')} className="px-4 py-2 border rounded">Save reduction preferences</button>
    </div>}
  </section>;
}
